/**
 * PATH: packages/ui/src/shortcuts/binding.ts
 * FILE_ID: UI-SHORTCUTS-BINDING-001
 * PURPOSE: Pure parse / match / format for normalized Binding strings.
 *          Turns 'mod+shift+s' into structured ChordSteps, matches a single
 *          chord against a KeyboardEvent (platform-aware), and renders a
 *          binding back into display keycaps.
 * WHY: Centralizes all key normalization (modifier order, ⌘/Ctrl, key
 *      aliases) in ONE tested place so the dispatcher and the reference agree
 *      and never drift.
 *
 * FLOW:
 *   parse:  'mod+k'        → [{ mod, ...flags, key:'k' }]
 *   match:  (step, event)  → boolean (mod→meta on mac / ctrl elsewhere)
 *   format: 'mod+k','cross'→ ['⌘/Ctrl','K']
 *
 * RELATED:
 *   - ./types.ts: Binding, Platform, DisplayPlatform
 *   - ./dispatcher.ts: consumes matchChordStep
 */

import type { Binding, DisplayPlatform, Platform } from './types'

export interface ChordStep {
  mod: boolean // platform meta: ⌘ on mac, Ctrl elsewhere
  ctrl: boolean // literal Control (distinct from mod)
  shift: boolean
  alt: boolean
  /** Canonical lowercased key token, e.g. 'k', 'enter', 'escape', 'comma'. */
  key: string
}

const MODIFIER_TOKENS: Record<string, keyof Omit<ChordStep, 'key'>> = {
  mod: 'mod',
  cmd: 'mod',
  command: 'mod',
  meta: 'mod',
  ctrl: 'ctrl',
  control: 'ctrl',
  shift: 'shift',
  alt: 'alt',
  option: 'alt',
  opt: 'alt',
}

/** Normalize a raw key token (from a binding or an event) to a canonical id. */
export function canonicalKey(raw: string): string {
  const k = raw.trim().toLowerCase()
  const aliases: Record<string, string> = {
    esc: 'escape',
    ',': 'comma',
    arrowup: 'up',
    arrowdown: 'down',
    arrowleft: 'left',
    arrowright: 'right',
    ' ': 'space',
    spacebar: 'space',
    del: 'delete',
    return: 'enter',
  }
  return aliases[k] ?? k
}

/**
 * parseBinding — split a binding string into ordered ChordSteps. Single chord
 * → one step; space-separated sequence → multiple. Returns [] for empty input.
 * Pure.
 */
export function parseBinding(binding: Binding | null | undefined): ChordStep[] {
  if (!binding) return []
  const steps: ChordStep[] = []
  for (const rawStep of binding.trim().split(/\s+/)) {
    if (!rawStep) continue
    const parts = rawStep.split('+').map((p) => p.trim()).filter(Boolean)
    const step: ChordStep = { mod: false, ctrl: false, shift: false, alt: false, key: '' }
    for (const part of parts) {
      const mod = MODIFIER_TOKENS[part.toLowerCase()]
      if (mod) step[mod] = true
      else step.key = canonicalKey(part)
    }
    if (step.key) steps.push(step)
  }
  return steps
}

/** True when the binding is a single chord (the only kind dispatched in v1). */
export function isSingleChord(binding: Binding | null | undefined): boolean {
  return parseBinding(binding).length === 1
}

/**
 * eventToStep — derive the canonical ChordStep from a KeyboardEvent on a given
 * platform. `mod` is true when the platform's primary modifier is held.
 * Pure given the event + platform.
 */
export function eventToStep(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
  platform: Platform,
): ChordStep {
  const primaryHeld = platform === 'mac' ? event.metaKey : event.ctrlKey
  const secondaryHeld = platform === 'mac' ? event.ctrlKey : false
  return {
    mod: primaryHeld,
    ctrl: secondaryHeld,
    shift: event.shiftKey,
    alt: event.altKey,
    key: canonicalKey(event.key),
  }
}

/**
 * matchChordStep — does a parsed binding step equal the event's step? All
 * modifier flags must match exactly (so 'k' does NOT fire on ⌘K). Pure.
 */
export function matchChordStep(
  step: ChordStep,
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
  platform: Platform,
): boolean {
  const ev = eventToStep(event, platform)
  return (
    step.mod === ev.mod &&
    step.ctrl === ev.ctrl &&
    step.shift === ev.shift &&
    step.alt === ev.alt &&
    step.key === ev.key
  )
}

/**
 * matchBinding — match a single-chord binding against an event. Multi-step
 * sequences return false here (sequence dispatch is deferred to v1.1; the
 * provider owns any buffering). Pure.
 */
export function matchBinding(
  binding: Binding | null | undefined,
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
  platform: Platform,
): boolean {
  const steps = parseBinding(binding)
  if (steps.length !== 1) return false
  return matchChordStep(steps[0], event, platform)
}

/** Keys that are modifiers themselves — a chord is incomplete until a real
 *  key is pressed alongside them. Used by eventToBinding to ignore the
 *  intermediate keydowns while the user is still holding modifiers. */
const MODIFIER_ONLY_KEYS = new Set([
  'meta', 'control', 'shift', 'alt', 'os', 'altgraph', 'capslock', 'fn',
  'fnlock', 'hyper', 'super', 'symbol', 'symbollock', 'contextmenu',
  'dead', 'unidentified', 'process', '',
])

/**
 * stepToBinding — serialize a ChordStep back to a canonical binding string,
 * using the same modifier order (mod, ctrl, alt, shift) as the override
 * comparison key so a captured remap round-trips through parse/format. Pure.
 */
export function stepToBinding(step: ChordStep): string {
  return [
    step.mod && 'mod',
    step.ctrl && 'ctrl',
    step.alt && 'alt',
    step.shift && 'shift',
    step.key,
  ]
    .filter(Boolean)
    .join('+')
}

/**
 * eventToBinding — derive a normalized single-chord binding from a captured
 * KeyboardEvent (used by the customization editor's "press keys" remap). Returns
 * null while only modifiers are held (so the editor keeps waiting). Pure given
 * the event + platform.
 */
export function eventToBinding(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>,
  platform: Platform,
): Binding | null {
  const step = eventToStep(event, platform)
  if (MODIFIER_ONLY_KEYS.has(step.key)) return null
  return stepToBinding(step)
}

const KEY_DISPLAY: Record<string, string> = {
  comma: ',',
  enter: 'Enter',
  escape: 'Escape',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  tab: 'Tab',
  space: 'Space',
  delete: 'Delete',
  backspace: 'Backspace',
  home: 'Home',
  end: 'End',
}

function displayMod(platform: DisplayPlatform): string {
  if (platform === 'cross') return '⌘/Ctrl'
  return platform === 'mac' ? '⌘' : 'Ctrl'
}

function displayKeyToken(key: string): string {
  if (KEY_DISPLAY[key]) return KEY_DISPLAY[key]
  // Single letters/digits → uppercase; everything else verbatim.
  return key.length === 1 ? key.toUpperCase() : key
}

/**
 * formatBinding — render a binding into ordered display keycaps, e.g.
 * formatBinding('mod+k','cross') → ['⌘/Ctrl','K']. Sequences are joined with
 * a lowercase 'then' separator. Returns [] for a null/empty binding. Pure.
 */
export function formatBinding(
  binding: Binding | null | undefined,
  platform: DisplayPlatform = 'cross',
): string[] {
  const steps = parseBinding(binding)
  if (steps.length === 0) return []
  const out: string[] = []
  steps.forEach((step, i) => {
    if (i > 0) out.push('then')
    if (step.mod) out.push(displayMod(platform))
    if (step.ctrl) out.push('Ctrl')
    if (step.alt) out.push(platform === 'mac' ? '⌥' : 'Alt')
    if (step.shift) out.push('Shift')
    out.push(displayKeyToken(step.key))
  })
  return out
}
